import { isEmpty } from 'lodash';
import { createSelector } from '@reduxjs/toolkit';
import { ProfileTypes } from '@sda/enums';
import { getNormalizedProfile } from '@sda/utils/meta';
import { pickPrimitives } from '@sda/utils/type';
import { getDateTime } from '@sda/utils/date';
import { getActPath, getVenuePath } from '@sda/utils/url';
import { getCoverImage, getImageThumbUrl } from '@sda/utils/image';
import {
  actSelectors, profileSelectors, sessionUserSelector, venueSelectors,
} from '..';
import { getSessionUserAdminStatus } from './login';
import type { PendingProfile } from '@sda/interfaces/profile';
import type { FetchDataType } from '@sda/sdk/interfaces';
import type { OptionalUnion, OptionalUnionTriple } from '@sda/helpers/helpers.types';
import type { Venue } from '@sda/interfaces/venue';
import type { Act } from '@sda/interfaces/act';

/**
 * Gets the selected profile ID for the logged in user
 * @param  {any} state
 * @return {string}
 */
export const getSelectedProfileId = createSelector(
  sessionUserSelector,
  (user) => user && user.selectedProfileId,
);

/**
 * Gets the selected profile type for the logged in user
 * @param  {any} state
 * @return {string}
 */
export const getSelectedProfileType = createSelector(
  sessionUserSelector,
  (user) => user && user.selectedProfileType,
);

export const getSelectedProfile = createSelector(
  sessionUserSelector,
  actSelectors.selectEntities,
  venueSelectors.selectEntities,
  profileSelectors.selectEntities,
  (user, acts, venues, pendingProfiles) => {
    if (!user) {
      return null;
    }

    const { selectedProfileId, selectedProfileType } = user;

    if (selectedProfileType === ProfileTypes.VENUE && venues && venues[selectedProfileId]) {
      return getNormalizedProfile<OptionalUnionTriple<Act, Venue, PendingProfile>>(ProfileTypes.VENUE, venues[selectedProfileId]);
    }

    if (selectedProfileType === ProfileTypes.ACT && acts && acts[selectedProfileId]) {
      return getNormalizedProfile<OptionalUnionTriple<Act, Venue, PendingProfile>>(ProfileTypes.ACT, acts[selectedProfileId]);
    }

    if (selectedProfileType === ProfileTypes.PENDING && pendingProfiles && pendingProfiles[selectedProfileId]) {
      return getNormalizedProfile<OptionalUnionTriple<Act, Venue, PendingProfile>>(ProfileTypes.PENDING, pendingProfiles[selectedProfileId]);
    }

    return null;
  },
);

/**
 * returns a flattened version of getSelectedProfile
 */
export const getSelectedProfileFlat = createSelector(
  getSelectedProfile,
  (profile) => {
    if (!profile) {
      return null;
    }

    const flatProfile = pickPrimitives(profile);

    const result = {
      ...flatProfile,
      createdAt: getDateTime(profile.createdAt).toSeconds(),
      updatedAt: getDateTime(profile.updatedAt).toSeconds(),
      numCollaborators: profile.collaborators ? profile.collaborators.length : 0,
    };

    return result;
  },
);

/**
 * Returns the vanityUrl path for the selected profile
 */
export const getSelectedProfilePath = createSelector(
  getSelectedProfile,
  (profile) => {
    if (!profile || profile.profileType === ProfileTypes.PENDING) {
      return null;
    }

    if (profile.profileType === ProfileTypes.ACT) {
      return getActPath(profile.vanityUrl);
    }

    return getVenuePath(profile.vanityUrl);
  },
);

/**
 * Retrieves the avatar/publicImageUrl of the selected profile
 */
export const getSelectedProfileAvatar = createSelector(
  getSelectedProfile,
  (profile) => {
    if (!profile) {
      return null;
    }

    if (profile.profileType === ProfileTypes.ACT) {
      return getCoverImage(profile.publicImageUrl, null, getImageThumbUrl);
    }

    return getCoverImage(profile.publicImageUrl, null, getImageThumbUrl);
  },
);

/**
 * Retrieves whether or not the current profile is considered "complete" for the sake of responding to show calls or creating them
 */
export const getSelectedProfileIsCompleted = createSelector(
  getSelectedProfile,
  (profile) => {
    if (!profile) {
      return false;
    }

    if (profile.description && !isEmpty(profile.publicImageUrl)) {
      return true;
    }

    return false;
  },
);

/**
 * Retrieves whether or not the current profile is considered to be "vetted"
 * Note that this always returns true for artist profiles
 */
export const getSelectedProfileIsVetted = createSelector(
  getSelectedProfile,
  getSessionUserAdminStatus,
  (profile, isAdmin) => {
    if (isAdmin) {
      return true;
    }

    if (!profile) {
      return false;
    }

    if (profile.profileType === ProfileTypes.ACT) {
      return !profile.isRejected;
    }

    if (profile.profileType === ProfileTypes.PENDING) {
      return true;
    }

    return !!profile.isVetted;
  },
);

export const getSelectedProfileFeaturedReviewId = createSelector(
  getSelectedProfile,
  (profile) => {
    if (!profile) {
      return null;
    }

    return profile.featuredReviewId;
  },
);

export const getSelectedProfilePendingShowReview = createSelector(
  getSelectedProfile,
  (profile) => {
    if (!profile) {
      return false;
    }

    // getNormalizedProfile is only typed to return the PublicAct/Venue, but getSelectedProfile has access to the full Act/Venue
    const selectedProfile = profile as unknown as OptionalUnion<Act, Venue> & FetchDataType;

    return selectedProfile.pendingShowReviewId;
  },
);
